import React from 'react';
import styled from 'styled-components';
import { SketchPicker } from 'react-color';

const PropertyPanelContainer = styled.div`
  background-color:rgb(255, 255, 255);
  color:rgb(110, 110, 110);
  padding: 10px;
  border-radius: 5px;
  width: 250px;
  height: 100%;
  overflow-y: auto;
`;

const PanelTitle = styled.h3`
  margin-top: 0;
  padding-bottom: 10px;
  border-bottom: 1px solid #444;
`;

const PropertyGroup = styled.div`
  margin-bottom: 15px;
`;

const PropertyLabel = styled.label`
  display: block;
  margin-bottom: 5px;
  font-weight: 500;
`;

const PropertyInput = styled.input`
  width: 100%;
  padding: 8px;
  background-color: #333;
  border: 1px solid #444;
  border-radius: 4px;
  color: white;
  margin-bottom: 10px;
`;

const PropertySelect = styled.select`
  width: 100%;
  padding: 8px;
  background-color: #333;
  border: 1px solid #444;
  border-radius: 4px;
  color: white;
  margin-bottom: 10px;
`;

const PropertySlider = styled.input`
  width: 100%;
  margin-bottom: 10px;
`;

const ColorPickerContainer = styled.div`
  margin-bottom: 10px;
`;

const ColorPreview = styled.div`
  width: 100%;
  height: 30px;
  border-radius: 4px;
  background-color: ${props => props.color};
  cursor: pointer;
  border: 1px solid #444;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 10px;
`;

const Button = styled.button`
  flex: 1;
  padding: 8px;
  background-color: #4a90e2;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  &:hover {
    background-color: #357ae8;
  }
`;

interface AlignmentButtonProps {
  active: boolean;
}

const AlignmentButton = styled(Button)<AlignmentButtonProps>`
  background-color: ${props => props.active ? '#4a90e2' : '#333'};
  &:hover {
    background-color: ${props => props.active ? '#357ae8' : '#444'};
  }
`;

const PresetButton = styled.button`
  padding: 8px;
  margin-bottom: 5px;
  width: 100%;
  background-color: #333;
  color: white;
  border: 1px solid #444;
  border-radius: 4px;
  cursor: pointer;
  text-align: left;
  &:hover {
    background-color: #444;
  }
`;

interface PropertyPanelProps {
  selectedObject: any;
  onUpdateText: (text: string) => void;
  onUpdateFontFamily: (font: string) => void;
  onUpdateFontSize: (size: number) => void;
  onUpdateTextAlign: (align: string) => void;
  onUpdateColor: (color: string) => void;
  onUpdateOpacity: (opacity: number) => void;
  onUpdateBackgroundColor: (color: string) => void;
  onApplyTextPreset: (preset: any) => void;
  availableFonts: string[];
  textPresets: any[];
}

const PropertyPanel: React.FC<PropertyPanelProps> = ({
  selectedObject,
  onUpdateText,
  onUpdateFontFamily,
  onUpdateFontSize,
  onUpdateTextAlign,
  onUpdateColor,
  onUpdateOpacity,
  onUpdateBackgroundColor,
  onApplyTextPreset,
  availableFonts,
  textPresets
}) => {
  const [showColorPicker, setShowColorPicker] = React.useState(false);
  const [showBackgroundColorPicker, setShowBackgroundColorPicker] = React.useState(false);

  if (!selectedObject) {
    return (
      <PropertyPanelContainer>
        <PanelTitle>Propiedades</PanelTitle>
        <p>Selecciona un elemento para editar sus propiedades</p>
      </PropertyPanelContainer>
    );
  }

  const isText = selectedObject.type === 'text';
  const isImage = selectedObject.type === 'image';
  const isBackground = selectedObject.type === 'background';

  return (
    <PropertyPanelContainer>
      <PanelTitle>Propiedades</PanelTitle>
      
      {isText && (
        <>
          <PropertyGroup>
            <PropertyLabel>Texto</PropertyLabel>
            <PropertyInput
              type="text"
              value={selectedObject.text}
              onChange={(e) => onUpdateText(e.target.value)}
            />
          </PropertyGroup>

          <PropertyGroup>
            <PropertyLabel>Fuente</PropertyLabel>
            <PropertySelect
              value={selectedObject.fontFamily}
              onChange={(e) => onUpdateFontFamily(e.target.value)}
            >
              {availableFonts.map((font) => (
                <option key={font} value={font}>{font}</option>
              ))}
            </PropertySelect>
          </PropertyGroup>

          <PropertyGroup>
            <PropertyLabel>Tamaño: {selectedObject.fontSize}px</PropertyLabel>
            <PropertySlider
              type="range"
              min="10"
              max="150"
              value={selectedObject.fontSize}
              onChange={(e) => onUpdateFontSize(parseInt(e.target.value))}
            />
          </PropertyGroup>

          <PropertyGroup>
            <PropertyLabel>Alineación</PropertyLabel>
            <ButtonGroup>
              <AlignmentButton
                active={selectedObject.textAlign === 'left'}
                onClick={() => onUpdateTextAlign('left')}
              >
                Izquierda
              </AlignmentButton>
              <AlignmentButton
                active={selectedObject.textAlign === 'center'}
                onClick={() => onUpdateTextAlign('center')}
              >
                Centro
              </AlignmentButton>
              <AlignmentButton
                active={selectedObject.textAlign === 'right'}
                onClick={() => onUpdateTextAlign('right')}
              >
                Derecha
              </AlignmentButton>
            </ButtonGroup>
          </PropertyGroup>

          <PropertyGroup>
            <PropertyLabel>Color</PropertyLabel>
            <ColorPickerContainer>
              <ColorPreview
                color={selectedObject.fill}
                onClick={() => setShowColorPicker(!showColorPicker)}
              />
              {showColorPicker && (
                <SketchPicker
                  color={selectedObject.fill}
                  onChange={(color) => onUpdateColor(color.hex)}
                />
              )}
            </ColorPickerContainer>
          </PropertyGroup>

          <PropertyGroup>
            <PropertyLabel>Presets de Texto</PropertyLabel>
            {textPresets.map((preset, index) => (
              <PresetButton
                key={index}
                onClick={() => onApplyTextPreset(preset)}
              >
                {preset.name}
              </PresetButton>
            ))}
          </PropertyGroup>
        </>
      )}

      {isImage && (
        <>
          <PropertyGroup>
            <PropertyLabel>Opacidad: {Math.round(selectedObject.opacity * 100)}%</PropertyLabel>
            <PropertySlider
              type="range"
              min="0"
              max="100"
              value={Math.round(selectedObject.opacity * 100)}
              onChange={(e) => onUpdateOpacity(parseInt(e.target.value) / 100)}
            />
          </PropertyGroup>
        </>
      )}

      {isBackground && (
        <PropertyGroup>
          <PropertyLabel>Color de Fondo</PropertyLabel>
          <ColorPickerContainer>
            <ColorPreview
              color={selectedObject.fill}
              onClick={() => setShowBackgroundColorPicker(!showBackgroundColorPicker)}
            />
            {showBackgroundColorPicker && (
              <SketchPicker
                color={selectedObject.fill}
                onChange={(color) => onUpdateBackgroundColor(color.hex)}
              />
            )}
          </ColorPickerContainer>
        </PropertyGroup>
      )}
    </PropertyPanelContainer>
  );
};

export default PropertyPanel;
